import * as React from 'react';
import { format } from 'date-fns';
import { FragmentType, graphql, useFragment } from '../../../__generated__';
import { displayMoney } from 'gmd-utilities-package';
import useCommonNavigations from '../../../hooks/Navigation';
import TableRow, { TableAction } from './TableRow';
import { useMutation } from '@apollo/client';
import { useLocation } from 'react-router';
import {
  duplicatedTransaction,
  confirmTransactionMutation,
  submitTransactionMutation,
  refreshRequestsVar,
  confirmAndSubmitTransactionMutation,
} from '../common';
import { TransactionFormFragment } from '../agent/RequestForm';

export const TransactionRowTransactionFragment = graphql(/* GraphQL */ `
  fragment Transaction_RowFragment on Transaction {
    dateCreated
    relevantAgent {
      email
    }
    state
    errors {
      code
      message
    }
    ...TransactionFormTransaction
  }
`);

export const TransactionResponseFragment = graphql(/* GraphQL */ `
  fragment TransactionResponseFragment on TransactionResponse {
    code
    message
    success
    transaction {
      ...Transaction_RowFragment
    }
  }
`);

export type TransactionRowProps = {
  transaction: FragmentType<typeof TransactionRowTransactionFragment>;
};

const TransactionRow = (props: TransactionRowProps) => {
  const trx1 = useFragment(
    TransactionRowTransactionFragment,
    props.transaction
  );
  const trx = useFragment(TransactionFormFragment, trx1);
  const { goToAgentRequest, goToRequest, goToReceipt } = useCommonNavigations();
  const [confirmTransaction, confirmResult] = useMutation(
    confirmTransactionMutation
  );
  const [submitTransaction, submitResult] = useMutation(
    submitTransactionMutation
  );
  const [confirmAndSubmitTransaction, confirmAndSubmitResult] = useMutation(
    confirmAndSubmitTransactionMutation
  );
  const senderName = React.useMemo(() => {
    return trx?.historicals?.senderName || trx.sender?.name || 'Unkown';
  }, [trx?.historicals?.senderName, trx.sender?.name]);

  const recipientName = React.useMemo(() => {
    return trx?.historicals?.recipientName || trx.recipient?.name || 'Unkown';
  }, [trx?.historicals?.recipientName, trx.recipient?.name]);
  const location = useLocation();

  let uiState = trx1.state || '';

  const isLoading =
    confirmResult.loading ||
    submitResult.loading ||
    confirmAndSubmitResult.loading ||
    (uiState.length > 0 && uiState !== 'ERRORED');

  const editAction: TableAction = {
    action: () => {
      goToRequest(trx.id, location.pathname);
    },
    display: {
      tooltip: 'Edit',
      aria: 'edit',
      iconType: 'EDIT',
    },
  };
  const confirmAction: TableAction = {
    action: () => confirmTransaction({ variables: { id: trx.id } }),
    display: {
      tooltip: 'Confirm Transaction',
      aria: 'confirm',
      iconType: 'CONFIRM',
    },
  };
  const confirmAndSubmitAction: TableAction = {
    action: () => confirmAndSubmitTransaction({ variables: { id: trx.id } }),
    display: {
      tooltip: 'Confirm and Submit Transaction',
      aria: 'confirm and submit',
      iconType: 'CONFIRM_AND_SUBMIT',
    },
  };
  const duplicateAction: TableAction = {
    action: () => {
      duplicatedTransaction(trx1);
      goToAgentRequest(location.pathname);
    },
    display: {
      tooltip: 'Duplicate',
      aria: 'copy',
      iconType: 'COPY',
    },
  };

  const getActions = (): TableAction[] => {
    switch (trx.status) {
      case 'REQUESTED':
        return [
          editAction,
          confirmAction,
          confirmAndSubmitAction,
          duplicateAction,
        ];
      case 'CONFIRMED':
        return [
          editAction,
          {
            action: () => {
              submitTransaction({ variables: { id: trx.id } });
              refreshRequestsVar(true);
            },
            display: {
              tooltip: 'Submit Transaction',
              aria: 'send',
              iconType: 'SEND',
            },
          },
          duplicateAction,
        ];
      case 'PENDING':
        return [editAction, duplicateAction];
      case 'SUCCESS':
      case 'ERRORED':
      case 'REVERSED':
        return [
          {
            action: () => goToRequest(trx.id, location.pathname),
            display: {
              tooltip: 'View',
              aria: 'view',
              iconType: 'VIEW',
            },
          },
          duplicateAction,
          {
            action: () => goToReceipt(trx.id, location.pathname),
            display: {
              tooltip: 'Receipt',
              aria: 'receipt',
              iconType: 'RECEIPT',
            },
          },
        ];
      default:
        return [];
    }
  };

  return (
    <TableRow
      values={{
        date: format(new Date(parseInt(trx1.dateCreated)), 'd MMM, HH:mm'),
        id: trx.id,
        send: displayMoney(trx.sent.amount, trx.sent.currency),
        receive: displayMoney(trx.received.amount, trx.received.currency),
        sender: trx.sender ? `${senderName} (${trx.sender.phone})` : 'Unknown',
        recipient: trx.recipient
          ? `${recipientName} (${trx.recipient.phone})`
          : 'Unknown',
        exchangeRate: `${trx.exchangeRate}`,
        agentEmail: trx1.relevantAgent?.email || 'Unknown',
        status: trx.status,
      }}
      actions={getActions()}
      disabled={isLoading}
      key={trx.id}
    />
  );
};

export default TransactionRow;
